import {
  ApiTriggerType,
  ApplicationScope,
  Dimension,
} from "@superblocksteam/shared";
import { call, put, select } from "redux-saga/effects";
import { createNameValidator } from "hooks/store/useEntityNameValidator";
import {
  deleteWidgetProperty,
  updateWidgetProperties,
} from "legacy/actions/controlActions";
import {
  addSectionWidgetColumn,
  deleteWidgets,
} from "legacy/actions/widgetActions";
import { ReduxActionTypes } from "legacy/constants/ReduxActionConstants";
import { APP_MODE } from "legacy/reducers/types";
import { getAppMode } from "legacy/selectors/applicationSelectors";
import { getDataTreeWidgetsById } from "legacy/selectors/dataTreeSelectors";
import {
  getFlattenedCanvasWidgets,
  getCurrentApplicationId,
  getCurrentPageId,
} from "legacy/selectors/editorSelectors";
import { getWidget, getWidgets } from "legacy/selectors/entitiesSelector";
import { getDynamicLayoutWidgets } from "legacy/selectors/layoutSelectors";
import { getRoutesList } from "legacy/selectors/routeSelectors";
import { getWidgetsMeta } from "legacy/selectors/sagaSelectors";
import { selectGeneratedTheme } from "legacy/selectors/themeSelectors";
import { getStore } from "store/dynamic";
import renameApplicationEntity from "store/sagas/renameApplicationEntity";
import {
  selectAiApiStatuses,
  selectAiWidgetChangesById,
} from "store/slices/ai/selectors";
import { createV2ApiSaga } from "store/slices/apisV2";
import { selectAllV2Apis } from "store/slices/apisV2";
import { updateV2ApiSaga } from "store/slices/apisV2/sagas/updateV2Api";
import { V2ApiPayloadCreators } from "store/slices/apisV2/utils/api-utils";
import {
  createEvent,
  updateCustomEvent,
} from "store/slices/application/events/eventActions";
import { AppStateVarPersistance } from "store/slices/application/stateVars/StateConstants";
import {
  createStateVar,
  updateStateVars,
} from "store/slices/application/stateVars/stateVarsActions";
import {
  createTimer,
  updateTimers,
} from "store/slices/application/timers/timerActions";
import { selectFlags } from "store/slices/featureFlags";
import { selectOnlyOrganizationId } from "store/slices/organizations/selectors";
import { processActionsIntoChanges } from "../processActionsIntoChanges";
import { setAiChanges, setApiStatus } from "../slice";
import { ClarkFocusedEntityType } from "../types";
import type { ClarkProcessorResult } from "../ClarkProcessor";
import type { processAiActions } from "../slice";
import type { ClarkActionFuntions } from "../types";
import type {
  WidgetAddChild,
  WidgetAddSectionColumn,
  WidgetDelete,
  WidgetMove,
} from "legacy/actions/pageActions";
import type { ReduxAction } from "legacy/constants/ReduxActionConstants";
import type {
  WidgetWidthModes,
  WidgetHeightModes,
} from "legacy/constants/WidgetConstants";
import type {
  DataTree,
  DataTreeWidget,
} from "legacy/entities/DataTree/dataTreeFactory";
import type { WidgetMetadata } from "legacy/reducers/entityReducers/metaReducer";
import type { WidgetProps } from "legacy/widgets/BaseWidget/types";
import type { CanvasWidgetsReduxState } from "legacy/widgets/Factory";
import type { UpdateV2ApiPayload } from "store/slices/apisV2/sagas/updateV2Api";
import type { ApiDtoWithPb } from "store/slices/apisV2/slice";
import type { AllFlags } from "store/slices/featureFlags";
import type { AppState } from "store/types";

let lastClarkRequestId: string | null = null;
let actionQueue: any[] = [];

export function* processAiActionsSaga(
  action: ReturnType<typeof processAiActions>,
) {
  const appMode: APP_MODE = (yield select(getAppMode)) ?? APP_MODE.EDIT;
  const theme: ReturnType<typeof selectGeneratedTheme> =
    yield select(selectGeneratedTheme);
  const dataTree: DataTree = yield select(
    (state) => state.legacy.evaluations.tree,
  );
  const routes: ReturnType<typeof getRoutesList> = yield select(getRoutesList);
  const state: AppState = yield select((state) => state);
  const featureFlags: Partial<AllFlags> = yield select(selectFlags);

  const widgets: Record<string, Partial<WidgetProps>> = yield select(
    getWidgets,
  );
  const dynamicWidgetLayout: ReturnType<typeof getDynamicLayoutWidgets> =
    yield select(getDynamicLayoutWidgets);

  const widgetsRuntime: CanvasWidgetsReduxState = yield select(
    getFlattenedCanvasWidgets,
  );
  const widgetMetaProps: WidgetMetadata = yield select(getWidgetsMeta);
  const evaluatedWidgets: Record<string, DataTreeWidget> = yield select(
    getDataTreeWidgetsById,
  );
  const previousAiWidgets: Record<string, DataTreeWidget> = yield select(
    selectAiWidgetChangesById,
  );
  const previousApiStatuses: Record<string, "STARTED" | "COMPLETED"> = yield (
    select(selectAiApiStatuses) || {}
  );

  const applicationId: string = yield select(getCurrentApplicationId);
  const organizationId: string = yield select(selectOnlyOrganizationId);
  const pageId: string = yield select(getCurrentPageId);
  const apisV2: Record<string, ApiDtoWithPb> = yield select(selectAllV2Apis);
  const getState: () => AppState = () => getStore().getState();

  const validator = createNameValidator(state);

  if (action.payload.clarkRequestId !== lastClarkRequestId) {
    actionQueue = [];
    lastClarkRequestId = action.payload.clarkRequestId;
  }

  const createComponentFn: ClarkActionFuntions["createComponentFn"] = ({
    parentWidgetId,
    widgetType,
    newWidgetId,
    initialProps,
  }) => {
    const createWidgetAction = {
      type: ReduxActionTypes.WIDGET_CREATE,
      payload: {
        widgetId: parentWidgetId,
        type: widgetType,
        newWidgetId,
        widgetName: initialProps.widgetName,
        size: {
          height: initialProps.height as Dimension<WidgetHeightModes>,
          width: initialProps.width as Dimension<WidgetWidthModes>,
        },
        position: {
          top: initialProps.top ?? Dimension.gridUnit(0),
          left: initialProps.left ?? Dimension.gridUnit(0),
        },
        props: {
          ...(initialProps || {}),
        } satisfies Partial<WidgetProps>,
        skipSave: true,
      },
    } satisfies ReduxAction<WidgetAddChild>;

    actionQueue.push(createWidgetAction);
  };

  const renameEntityFn: ClarkActionFuntions["renameEntityFn"] = ({
    entityId,
    oldName,
    newName,
  }) => {
    const renameApplicationEntityAction = renameApplicationEntity.apply({
      entityId,
      oldName,
      newName,
      scope: ApplicationScope.PAGE,
    }).start;
    actionQueue.push(renameApplicationEntityAction);
  };

  const updateComponentFn: ClarkActionFuntions["updateComponentFn"] = ({
    widgetId,
    widgetUpdates,
  }) => {
    const updateWidgetAction = updateWidgetProperties(widgetId, widgetUpdates);
    actionQueue.push(updateWidgetAction);
  };

  const resizeComponentFn: ClarkActionFuntions["resizeComponentFn"] = ({
    widgetId,
    size,
    position,
  }) => {
    actionQueue.push({
      type: ReduxActionTypes.WIDGET_RESIZE,
      payload: {
        widgetId,
        size,
        position,
      },
    });
  };

  const deleteComponentFn: ClarkActionFuntions["deleteComponentFn"] = ({
    widgetId,
  }) => {
    const deleteWidgetAction = {
      type: deleteWidgets.type,
      payload: {
        widgetIds: [widgetId],
      },
    } satisfies ReduxAction<WidgetDelete>;
    actionQueue.push(deleteWidgetAction);
  };

  const addSectionColumnFn: ClarkActionFuntions["addSectionColumnFn"] = ({
    sectionWidgetId,
    columnProperties,
  }: {
    sectionWidgetId: string;
    columnProperties?: Record<string, any>;
  }) => {
    const addSectionColumnAction = {
      type: addSectionWidgetColumn.type,
      payload: {
        sectionWidgetId,
        columnProperties,
      },
    } satisfies ReduxAction<WidgetAddSectionColumn>;
    actionQueue.push(addSectionColumnAction);
  };

  const deleteComponentPropertiesFn: ClarkActionFuntions["deleteComponentPropertiesFn"] =
    ({ widgetId, propertyPaths }) => {
      const deleteWidgetPropertyAction = deleteWidgetProperty(
        widgetId,
        propertyPaths,
      );
      actionQueue.push(deleteWidgetPropertyAction);
    };

  const moveWidgetFn: ClarkActionFuntions["moveWidgetFn"] = ({
    widgetId,
    parentId,
    newChildIndex,
    position,
    newParentId,
    size,
  }) => {
    const moveWidgetAction = {
      type: ReduxActionTypes.WIDGETS_MOVE,
      payload: {
        widgetId,
        parentId,
        newChildIndex,
        position,
        newParentId,
        size,
        ignoreSelectedWidgets: true,
      },
    } satisfies ReduxAction<WidgetMove>;
    actionQueue.push(moveWidgetAction);
  };

  // api functions

  const createApiFn: ClarkActionFuntions["createApiFn"] = (
    api: ApiDtoWithPb,
  ) => {
    const createApiAction = {
      type: createV2ApiSaga.start.type,
      payload: {
        pageId,
        payload: V2ApiPayloadCreators[ApiTriggerType.UI]({
          apiId: api.id ?? "",
          name: api.name ?? "",
          initialBlocks: api.apiPb.blocks ?? [],
          organizationId,
          applicationId,
          pageId,
        }),
      },
    };

    actionQueue.push(createApiAction);
  };

  const updateApiFn: ClarkActionFuntions["updateApiFn"] = (
    updatedApi: ApiDtoWithPb,
  ) => {
    const updateV2ApiPayload: UpdateV2ApiPayload = {
      apiPb: updatedApi.apiPb,
      shouldUpdateUndoStack: true,
    };
    const updateApiAction = {
      type: updateV2ApiSaga.start.type,
      payload: updateV2ApiPayload,
    };

    actionQueue.push(updateApiAction);
  };

  const existingWidget: WidgetProps | undefined = yield select((state) =>
    action.payload.existingEntityType === ClarkFocusedEntityType.WIDGET
      ? getWidget(state, action.payload.existingEntityId)
      : undefined,
  );

  if (
    action.payload.existingEntityType === ClarkFocusedEntityType.WIDGET &&
    !existingWidget
  ) {
    throw new Error(
      `Widget with id not found: ${action.payload.existingEntityId}`,
    );
  }

  const createStateVarFn: ClarkActionFuntions["createStateVarFn"] = (
    variableName: string,
    variableId: string,
  ) => {
    const createStateVarAction = {
      type: createStateVar.type,
      payload: {
        name: variableName,
        scope: ApplicationScope.PAGE,
        persistance: AppStateVarPersistance.TEMPORARY,
        open: false,
        id: variableId,
      },
    };
    actionQueue.push(createStateVarAction);
  };

  const editStateVarFn: ClarkActionFuntions["editStateVarFn"] = ({
    variableName,
    variableId,
    property,
    value,
  }: {
    variableName: string;
    variableId: string;
    property: string;
    value: unknown;
  }) => {
    if (property === "name") {
      renameEntityFn({
        entityId: variableId,
        oldName: variableName,
        newName: value as string,
      });
    } else {
      const editStateVarAction = {
        type: updateStateVars.type,
        payload: {
          scope: ApplicationScope.PAGE,
          stateVars: {
            [variableId]: {
              [property]: value,
            },
          },
        },
      };
      actionQueue.push(editStateVarAction);
    }
  };

  const createTimerFn: ClarkActionFuntions["createTimerFn"] = (
    timerName: string,
    timerId: string,
  ) => {
    const createTimerAction = {
      type: createTimer.type,
      payload: {
        name: timerName,
        scope: ApplicationScope.PAGE,
        openInPropertyPane: false,
        id: timerId,
      },
    };
    actionQueue.push(createTimerAction);
  };

  const editTimerFn: ClarkActionFuntions["editTimerFn"] = ({
    timerId,
    property,
    value,
  }: {
    timerId: string;
    property: string;
    value: unknown;
  }) => {
    const editTimerAction = {
      type: updateTimers.type,
      payload: {
        [timerId]: {
          updates: {
            [property]: value,
          },
          scope: ApplicationScope.PAGE,
        },
      },
    };
    actionQueue.push(editTimerAction);
  };

  const createEventFn: ClarkActionFuntions["createEventFn"] = (
    eventName: string,
    eventId: string,
  ) => {
    const createEventAction = {
      type: createEvent.type,
      payload: {
        name: eventName,
        scope: ApplicationScope.PAGE,
        id: eventId,
      },
    };
    actionQueue.push(createEventAction);
  };

  const editEventFn: ClarkActionFuntions["editEventFn"] = ({
    eventId,
    property,
    value,
  }: {
    eventId: string;
    property: string;
    value: unknown;
  }) => {
    const editEventAction = {
      type: updateCustomEvent.type,
      payload: {
        scope: ApplicationScope.PAGE,
        updates: {
          [eventId]: {
            [property]: value,
          },
        },
      },
    };
    actionQueue.push(editEventAction);
  };

  for (const act of action.payload.actions) {
    // mark which apis are in progress
    if (
      act.action === "scaffoldApi" ||
      act.action === "editApi" ||
      act.action === "createApi"
    ) {
      const previousStatus = previousApiStatuses?.[act.apiName];
      if (!previousStatus) {
        yield put(setApiStatus({ apiName: act.apiName, status: "STARTED" }));
      }
    }
  }

  const {
    widgetChangedKeys,
    renamesById,
    widgetChanges,
    metadataByWidgetId,
    discardedEdits,
    apiChanges,
    apiStatuses,
    changedApiBlockPaths,
    stateVariableChanges,
    timerChanges,
    eventChanges,
    stateVariableChangedKeys,
    timerChangedKeys,
    eventChangedKeys,
    widgetMovesById,
    originalNamesById,
  }: ClarkProcessorResult & {
    apiStatuses: Record<string, "STARTED" | "COMPLETED">;
  } = yield call(processActionsIntoChanges, {
    clarkRequestId: action.payload.clarkRequestId,
    actions: action.payload.actions,
    dataTree,
    routes,
    theme,
    nameValidator: validator,
    featureFlags,
    widgets,
    apisV2,
    getState,
    focusedEntityId: action.payload.existingEntityId,
    appMode,
    dynamicWidgetLayout,
    widgetsRuntime,
    widgetMetaProps,
    evaluatedWidgets,
    previousAiWidgets,
    organizationId,
    applicationId,
    pageId,
    createComponentFn,
    updateComponentFn,
    deleteComponentFn,
    deleteComponentPropertiesFn,
    renameEntityFn,
    createApiFn,
    updateApiFn,
    addSectionColumnFn,
    createStateVarFn,
    editStateVarFn,
    createTimerFn,
    editTimerFn,
    createEventFn,
    editEventFn,
    moveWidgetFn,
    resizeComponentFn,
  });

  while (actionQueue.length > 0) {
    const action = actionQueue.shift();
    yield put(action);
  }

  yield put(
    setAiChanges({
      widgetChangedKeys,
      widgetChanges,
      renamesById,
      discardedEdits,
      metadataByWidgetId,
      apiChanges,
      changedApiBlockPaths,
      apiStatuses,
      stateVariableChanges,
      timerChanges,
      eventChanges,
      stateVariableChangedKeys,
      timerChangedKeys,
      eventChangedKeys,
      widgetMovesById,
      originalNamesById,
    }),
  );
  for (const act of action.payload.actions) {
    // mark which apis are finished (do this after the actions are processed)
    if (act.action === "completeApi") {
      const previousStatus = previousApiStatuses?.[act.apiName];
      if (previousStatus !== "COMPLETED") {
        yield put(setApiStatus({ apiName: act.apiName, status: "COMPLETED" }));
      }
    }
  }
}
